import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../components/home/Footer";
import Header from "../components/home/Header";
import MiniCart from "../components/home/MiniCart";
import SEOHead from "../components/seo/SEOHead";

const CONTACT_IMAGE =
  "https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e?auto=format&fit=crop&w=1400&q=80";
const CONTACT_API_URL = "";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  products: [],
  quantity: "",
  message: ""
};

function submitContactEnquiry(payload) {
  const dummyApiUrl = "/api/contact-enquiry";

  if (CONTACT_API_URL) {
    return fetch(CONTACT_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    }).then(async (response) => {
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || "Something went wrong while submitting your enquiry.");
      }

      return data;
    });
  }

  return new Promise((resolve, reject) => {
    window.setTimeout(() => {
      if (payload.email.toLowerCase().includes("error")) {
        reject(new Error("The dummy API could not accept this email address. Please try another one."));
        return;
      }

      resolve({
        ok: true,
        url: dummyApiUrl,
        enquiryId: `PO-${Date.now()}`
      });
    }, 900);
  });
}

function getValidationErrors(form) {
  const errors = {};
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!form.name.trim()) errors.name = "Please enter your name.";
  if (!form.email.trim()) {
    errors.email = "Please enter your email address.";
  } else if (!emailPattern.test(form.email.trim())) {
    errors.email = "Please enter a valid email address.";
  }
  if (!form.phone.trim()) errors.phone = "Please enter your phone number.";
  if (form.products.length === 0) errors.products = "Please choose at least one product.";

  return errors;
}

export default function ContactPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartItems] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Paras Organics",
    "url": "https://parasorganics.com/contact",
    "mainEntity": {
      "@type": "Organization",
      "name": "Paras Organics",
      "telephone": "+91 74050 01130",
      "email": "goparas@gmail.com",
      "address": "Vehlal, Naroda - Dehgam Road, Gandhinagar, Gujarat"
    }
  }), []);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: "" }));
    setApiError("");
  }

  function updateNumericField(field, value) {
    updateField(field, value.replace(/\D/g, ""));
  }

  function toggleProduct(product) {
    setForm((current) => {
      const products = current.products.includes(product)
        ? current.products.filter((item) => item !== product)
        : [...current.products, product];

      return { ...current, products };
    });
    setErrors((current) => ({ ...current, products: "" }));
    setApiError("");
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const nextErrors = getValidationErrors(form);
    setErrors(nextErrors);
    setApiError("");

    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);

    try {
      await submitContactEnquiry({
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim()
      });
      navigate("/contact/thank-you");
    } catch (error) {
      setApiError(error.message || "Something went wrong while submitting your enquiry.");
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <SEOHead
        title="Contact Us"
        description="Contact Paras Organics for Vermicompost and Vermiwash enquiries, bulk quantities, and organic fertilizer support."
        path="/contact"
        image={CONTACT_IMAGE}
        schema={schema}
      />
      <Header scrolled={scrolled} cartItems={cartItems} onCartClick={() => setCartOpen((open) => !open)} />
      {cartOpen && <MiniCart cartItems={cartItems} onClose={() => setCartOpen(false)} />}
      <main className="contact-page">
        <section className="contact-shell" aria-labelledby="contact-title">
          <div className="contact-grid">
            <div className="contact-media">
              <img src={CONTACT_IMAGE} alt="Organic farming field with healthy green crops" />
            </div>
            <div className="contact-form-panel">
              <span className="section-label">Contact Us</span>
              <h1 className="contact-title" id="contact-title">Tell us what your soil needs</h1>
              <p className="contact-intro">Share your product requirement and our team will get back with the right quantity guidance.</p>

              {apiError && <div className="contact-api-error" role="alert">{apiError}</div>}

              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="form-field">
                  <label htmlFor="contact-name">Name <span>*</span></label>
                  <input
                    id="contact-name"
                    type="text"
                    value={form.name}
                    onChange={(event) => updateField("name", event.target.value)}
                    aria-invalid={Boolean(errors.name)}
                    required
                  />
                  {errors.name && <p className="form-error">{errors.name}</p>}
                </div>

                <div className="form-field">
                  <label htmlFor="contact-email">Email <span>*</span></label>
                  <input
                    id="contact-email"
                    type="email"
                    value={form.email}
                    onChange={(event) => updateField("email", event.target.value)}
                    aria-invalid={Boolean(errors.email)}
                    required
                  />
                  {errors.email && <p className="form-error">{errors.email}</p>}
                </div>

                <div className="form-field">
                  <label htmlFor="contact-phone">Phone <span>*</span></label>
                  <input
                    id="contact-phone"
                    type="tel"
                    inputMode="numeric"
                    value={form.phone}
                    onChange={(event) => updateNumericField("phone", event.target.value)}
                    aria-invalid={Boolean(errors.phone)}
                    required
                  />
                  {errors.phone && <p className="form-error">{errors.phone}</p>}
                </div>

                <fieldset className="form-field product-options" aria-invalid={Boolean(errors.products)}>
                  <legend>Product <span>*</span></legend>
                  <label>
                    <input
                      type="checkbox"
                      checked={form.products.includes("Vermicompost")}
                      onChange={() => toggleProduct("Vermicompost")}
                    />
                    Vermicompost
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={form.products.includes("Vermiwash")}
                      onChange={() => toggleProduct("Vermiwash")}
                    />
                    Vermiwash
                  </label>
                  {errors.products && <p className="form-error">{errors.products}</p>}
                </fieldset>

                <div className="form-field">
                  <label htmlFor="contact-quantity">Approx. Quantity</label>
                  <input
                    id="contact-quantity"
                    type="text"
                    inputMode="numeric"
                    value={form.quantity}
                    onChange={(event) => updateNumericField("quantity", event.target.value)}
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="contact-message">Message</label>
                  <textarea
                    id="contact-message"
                    rows="4"
                    value={form.message}
                    onChange={(event) => updateField("message", event.target.value)}
                  />
                </div>

                <button className="btn-primary contact-submit" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
